import { z } from 'zod';

import { PromoterAgreementStatus } from './types';
import { buildQueryString } from '../../utils/querystring';
import { BrandReviewStatus } from '../brand/brand';
import { BrandInvitationStatus } from '../brand/brand-invitation';
import { BrandExperienceTypes, Industry } from '../brand/brand-marketing';
import { ProviderSignUpChannel } from '../brand/provider-sign-up';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema, sortOptions } from '../common';

const AgreementSchema = z.object({
  id: z.string().uuid(),
});

const CreatePartnerAgreementResponseSchema = z.object({
  currentBrandToPartnerAgreement: AgreementSchema.nullable(),
  partnerToCurrentBrandAgreement: AgreementSchema.nullable(),
});

export const CreatePartnerAgreementEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, partnerBrandId }: { brandId: string; partnerBrandId: string }) =>
    `v1/brands/${brandId}/partners/${partnerBrandId}`,
  responseSchema: CreatePartnerAgreementResponseSchema,
  requestSchema: z.void(),
});

export const GetPartnersQuerySchema = paginateQuerySchema({}).extend({
  status: z.nativeEnum(BrandReviewStatus).optional(),
});

export const GetPartnersItemSchema = z.object({
  promoterBrandId: z.string().uuid(),
  providerBrandId: z.string().uuid(),
  name: z.string(),
  emailAddress: z.string().email(),
  logoUrl: z.string().url().nullable(),
  identityImageUrl: z.string().url().nullable(),
  isBidirectional: z.boolean(),
});

export const GetPartnersResponseSchema = paginateResponseSchema(GetPartnersItemSchema);

export type GetPartnersQuery = z.infer<typeof GetPartnersQuerySchema>;
export type GetPartnersResponse = z.infer<typeof GetPartnersResponseSchema>;
export type GetPartnersItem = z.infer<typeof GetPartnersItemSchema>;

export const GetPartnersEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query, brandId }: { query: z.input<typeof GetPartnersQuerySchema>; brandId: string }) =>
    `v1/brands/${brandId}/partners?${buildQueryString(query)}`,
  responseSchema: GetPartnersResponseSchema,
  querySchema: GetPartnersQuerySchema,
  requestSchema: z.void(),
});

const DeletePartnerAgreementResponseSchema = z.object({
  success: z.boolean(),
});

export const DeletePartnerAgreementEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId, partnerBrandId }: { brandId: string; partnerBrandId: string }) =>
    `v1/brands/${brandId}/partners/${partnerBrandId}`,
  responseSchema: DeletePartnerAgreementResponseSchema,
  requestSchema: z.void(),
});

export const UpdatePromotedListingCommissionRequestSchema = z
  .object({
    promoterBrandId: z.string().uuid(),
    percentCommission: z.number().min(0).max(100).optional(),
    flatCommission: z.number().min(0).optional(),
    experienceId: z.string().uuid().optional(),
    isApproved: z.boolean(),
  })
  .refine((data) => (data.percentCommission != null) !== (data.flatCommission != null), {
    message: 'Provide either percentCommission or flatCommission, not both',
  });

export const UpdatePromotedListingCommissionResponseSchema = z.object({
  promoterAgreementId: z.string().uuid(),
  promotedListingId: z.string().uuid(),
});

const CreatePartnerInvitationRequestSchema = z.object({
  inviteeEmail: z.string().email(),
});

export type CreatePartnerInvitationRequest = z.infer<typeof CreatePartnerInvitationRequestSchema>;

export const CreatePartnerInvitationEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/partners/invite`,
  responseSchema: z.void(),
  requestSchema: CreatePartnerInvitationRequestSchema,
});

const GetPartnerInvitationsQuerySchema = z.object({
  status: z.nativeEnum(BrandReviewStatus).optional(),
  emailAddress: z.string().optional(),
  channel: z.nativeEnum(ProviderSignUpChannel).optional(),
});

const GetPartnerInvitationsResponseSchema = z.object({
  id: z.string().uuid(),
  resultingUserEmail: z.string().email(),
  status: z.nativeEnum(BrandReviewStatus),
});

export const GetPartnerInvitationsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetPartnerInvitationsQuerySchema> }) =>
    `v1/brands/${brandId}/partners/invite?${buildQueryString(query)}`,
  responseSchema: GetPartnerInvitationsResponseSchema,
  requestSchema: z.void(),
  querySchema: GetPartnerInvitationsQuerySchema,
});

const GetPartnerPipelineQuerySchema = paginateQuerySchema({}).extend({
  brandReviewStatus: z.nativeEnum(BrandReviewStatus).optional(),
  brandInvitationStatus: z.nativeEnum(BrandInvitationStatus).optional(),
  promoterAgreementStatus: z.nativeEnum(PromoterAgreementStatus).optional(),
  providerAgreementStatus: z.nativeEnum(PromoterAgreementStatus).optional(),
  channel: z.nativeEnum(ProviderSignUpChannel).optional(),
});

export const GetPartnerPipelineItemSchema = z.object({
  brandId: z.string().uuid().nullable(),
  brandName: z.string().nullable(),
  experienceTypes: z.array(z.nativeEnum(BrandExperienceTypes)),
  brandReviewStatus: z.nativeEnum(BrandReviewStatus).nullable(),
  brandInvitationStatus: z.nativeEnum(BrandInvitationStatus),
  promoterAgreementStatus: z.nativeEnum(PromoterAgreementStatus).nullable(),
  identityImageUrl: z.string().url().nullable(),
  inviteeEmail: z.string().email().nullable(),
});

export const GetPartnerPipelineResponseSchema = paginateResponseSchema(GetPartnerPipelineItemSchema);

export const GetPartnerPipelineEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetPartnerPipelineQuerySchema> }) =>
    `v1/brands/${brandId}/partners/pipeline?${buildQueryString(query)}`,
  responseSchema: GetPartnerPipelineResponseSchema,
  requestSchema: z.void(),
  querySchema: GetPartnerPipelineQuerySchema,
});

export const FindPaginatedPendingApprovalsSortBy = ['name'] as const;

export const FindPaginatedPendingApprovalsQuerySchema = paginateQuerySchema({
  sortBy: FindPaginatedPendingApprovalsSortBy,
}).extend({
  sortDir: z.enum(sortOptions).default('ASC'),
});

export const FindPaginatedPendingApprovalsItemSchema = z.object({
  agreementId: z.string().uuid(),
  brandId: z.string().uuid(),
  brandName: z.string(),
  brandProfileImage: z.string().nullable(),
  agreementStatus: z.nativeEnum(PromoterAgreementStatus),
});

export const FindPaginatedPendingApprovalsResponseSchema = paginateResponseSchema(FindPaginatedPendingApprovalsItemSchema);

export type FindPaginatedPendingApprovalsQuery = z.infer<typeof FindPaginatedPendingApprovalsQuerySchema>;
export type FindPaginatedPendingApprovalsResponse = z.infer<typeof FindPaginatedPendingApprovalsResponseSchema>;
export type FindPaginatedPendingApprovalsItem = z.infer<typeof FindPaginatedPendingApprovalsItemSchema>;

export const FindPaginatedPendingApprovalsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof FindPaginatedPendingApprovalsQuerySchema> }) =>
    `v1/brands/${brandId}/partners/pending-approvals?${buildQueryString(query)}`,
  responseSchema: FindPaginatedPendingApprovalsResponseSchema,
  querySchema: FindPaginatedPendingApprovalsQuerySchema,
  requestSchema: z.void(),
});

export const FindPaginatedOrganizationBrandsSortBy = ['name'] as const;

export const FindPaginatedOrganizationBrandsQuerySchema = paginateQuerySchema({
  sortBy: FindPaginatedOrganizationBrandsSortBy,
}).extend({
  sortDir: z.enum(sortOptions).default('ASC'),
  includeApproved: z
    .enum(['true', 'false'])
    .transform((val) => val === 'true')
    .default('false'),
});

export const FindPaginatedOrganizationBrandsItemSchema = z.object({
  brandId: z.string().uuid(),
  brandName: z.string(),
  brandProfileImage: z.string().nullable(),
  isApproved: z.boolean(),
});

export const FindPaginatedOrganizationBrandsResponseSchema = paginateResponseSchema(
  FindPaginatedOrganizationBrandsItemSchema,
).extend({
  organizationId: z.string().uuid().nullable(),
  organizationName: z.string().nullable(),
});

export type FindPaginatedOrganizationBrandsQuery = z.infer<typeof FindPaginatedOrganizationBrandsQuerySchema>;
export type FindPaginatedOrganizationBrandsResponse = z.infer<typeof FindPaginatedOrganizationBrandsResponseSchema>;
export type FindPaginatedOrganizationBrandsItem = z.infer<typeof FindPaginatedOrganizationBrandsItemSchema>;

export const FindPaginatedOrganizationBrandsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof FindPaginatedOrganizationBrandsQuerySchema> }) =>
    `v1/brands/${brandId}/partners/organization-brands?${buildQueryString(query)}`,
  responseSchema: FindPaginatedOrganizationBrandsResponseSchema,
  querySchema: FindPaginatedOrganizationBrandsQuerySchema,
  requestSchema: z.void(),
});

export const ReviewPartnerApprovalResponseSchema = z.object({
  agreementId: z.string().uuid(),
});

export type ReviewPartnerApprovalResponse = z.infer<typeof ReviewPartnerApprovalResponseSchema>;

export const ApprovePartnerEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, providerBrandId }: { brandId: string; providerBrandId: string }) =>
    `v1/brands/${brandId}/partners/${providerBrandId}/review/approve`,
  requestSchema: z.void(),
  responseSchema: ReviewPartnerApprovalResponseSchema,
});

export const DeclinePartnerEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, providerBrandId }: { brandId: string; providerBrandId: string }) =>
    `v1/brands/${brandId}/partners/${providerBrandId}/review/decline`,
  requestSchema: z.void(),
  responseSchema: ReviewPartnerApprovalResponseSchema,
});

export const GetPartnerOverviewResponseSchema = z.object({
  identityImage: z.string().url().nullable(),
  logo: z.string().url().nullable(),
  name: z.string(),
  owner: z.string(),
  email: z.string().email(),
  website: z.string().nullable(), // brand_marketing.website
  experienceTypes: z.array(z.string()).nullable(), // brand_marketing.experienceTypes
  experienceOverview: z.string().nullable(), // brand_marketing.experienceVision
  industry: z.nativeEnum(Industry), // brand_marketing.industry
  reach: z.string().nullable(), // campaign.reachSubtext
  promoterAgreementStatus: z.nativeEnum(PromoterAgreementStatus).nullable(), // promoter_agreement.status
  isInWayReview: z.boolean(), // true if brand was invited but has no agreement yet
  isBidirectional: z.boolean(),
  isCurrentBrandPromoter: z.boolean(),
});

export type PartnerOverviewResponse = z.infer<typeof GetPartnerOverviewResponseSchema>;

export const GetPartnerOverviewEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, partnerBrandId }: { brandId: string; partnerBrandId: string }) =>
    `v1/brands/${brandId}/partners/${partnerBrandId}/overview`,
  responseSchema: GetPartnerOverviewResponseSchema,
  requestSchema: z.void(),
});
